import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import moment from 'moment';
import { ElectionSelectInput } from '../../components';

const PRECEDENT_CONFIG_BY_ELECTION_TYPE = {
  general: {
    field: 'primaryPrecedent',
    expectedType: 'primary',
    label: 'primary'
  },
  'general-runoff': {
    field: 'runoffPrecedent',
    expectedType: 'general',
    label: 'general'
  },
  'primary-runoff': {
    field: 'runoffPrecedent',
    expectedType: 'primary',
    label: 'primary'
  }
};

export const getPrecedentConfig = (election) => {
  const config = PRECEDENT_CONFIG_BY_ELECTION_TYPE[election?.electionType];
  if (!config || election?.[config.field]) return null;
  return config;
};

export const LinkElectionPrecedentModal = ({
  open,
  onClose,
  election,
  feathers,
  onSaved
}) => {
  const [selectedElection, setSelectedElection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const precedentConfig = getPrecedentConfig(election);

  useEffect(() => {
    if (!open) return;
    setSelectedElection(null);
    setError(null);
  }, [open]);

  const electionQuery = useMemo(() => {
    if (!election?.state || !election?.date || !precedentConfig) return {};

    const electionDate = moment.utc(election.date);
    return {
      state: election.state,
      electionType: precedentConfig.expectedType,
      date: {
        $gte: electionDate.clone().subtract(1, 'year').toISOString(),
        $lt: electionDate.toISOString()
      }
    };
  }, [election, precedentConfig]);

  const handleSave = async () => {
    if (!selectedElection || !precedentConfig) return;

    setSaving(true);
    setError(null);
    try {
      await feathers.getService('elections').patch(election.key, {
        [precedentConfig.field]: selectedElection.key
      });
      await onSaved?.();
      onClose();
    } catch (err) {
      console.error('Error linking election precedent:', err);
      setError('Unable to link the election. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link {precedentConfig?.label} election</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a {precedentConfig?.label} election from {election?.stateName} within the year
          before this election.
        </Typography>
        <ElectionSelectInput
          name={precedentConfig?.field}
          value={selectedElection}
          onChange={(event) => setSelectedElection(event.target.value)}
          query={electionQuery}
          style={{ width: '100%' }}
        />
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!selectedElection || saving}
        >
          {saving ? 'Linking...' : 'Link election'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
